import { useParams, Link } from 'react-router-dom';
import {
  Megaphone,
  ArrowLeft,
  CheckCircle2,
  Clock,
  ExternalLink,
  Paperclip,
  Loader2,
  Users,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Button } from '@/app/components/ui/button';
import { Badge } from '@/app/components/ui/badge';
import {
  useSafetyCommunication,
  useAcknowledgementStatus,
  useAcknowledgeCommunication,
} from '@/hooks/use-safety-communications';
import { resolveMediaUrl } from '@/lib/api-client';
import { useOrgTimeZone } from '@/hooks/use-settings';
import { formatDateTimeInTimeZone } from '@/lib/date-utils';

export function CommunicationDetailPage() {
  const timeZone = useOrgTimeZone();
  const { id } = useParams<{ id: string }>();
  const commId = Number(id);

  const { data: comm, isLoading } = useSafetyCommunication(commId);
  const { data: ackStatus } = useAcknowledgementStatus(commId);
  const acknowledgeMutation = useAcknowledgeCommunication();

  if (isLoading || !comm) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 flex items-center justify-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  const handleAcknowledge = () => {
    acknowledgeMutation.mutate(commId);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      {/* Back link */}
      <Link
        to="/compliance/communications"
        className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Communications
      </Link>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-start gap-3">
          <Megaphone className="h-6 w-6 text-blue-500 mt-1 shrink-0" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{comm.title}</h1>
            <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
              <Badge variant="secondary">{comm.category_display}</Badge>
              <span>{comm.target_audience_display}</span>
              <span>{formatDateTimeInTimeZone(comm.sent_at, timeZone, { dateStyle: 'medium' })}</span>
              {comm.sent_by_username && <span>By: {comm.sent_by_username}</span>}
            </div>
          </div>
        </div>

        {comm.requires_acknowledgment && (
          <Button
            onClick={handleAcknowledge}
            disabled={acknowledgeMutation.isPending}
          >
            {acknowledgeMutation.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <CheckCircle2 className="h-4 w-4 mr-2" />
            )}
            {acknowledgeMutation.isSuccess ? 'Acknowledged' : 'Acknowledge'}
          </Button>
        )}
      </div>

      {/* Body */}
      <Card>
        <CardHeader>
          <CardTitle>Communication Content</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="prose prose-sm max-w-none whitespace-pre-wrap">{comm.body}</div>

          {/* Source URL */}
          {comm.source_url && (
            <div className="mt-4 pt-4 border-t">
              <a
                href={comm.source_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
              >
                <ExternalLink className="h-4 w-4" />
                View Source
              </a>
            </div>
          )}

          {/* Attachment */}
          {comm.attachment && (
            <div className="mt-4 pt-4 border-t">
              <a
                href={resolveMediaUrl(comm.attachment)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
              >
                <Paperclip className="h-4 w-4" />
                Download Attachment
              </a>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Acknowledgement Status */}
      {comm.requires_acknowledgment && ackStatus && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Acknowledgement Status
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-gray-900">
                  {ackStatus.total_targeted}
                </div>
                <div className="text-xs text-gray-500">Total Targeted</div>
              </div>
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <div className="text-2xl font-bold text-green-700">
                  {ackStatus.acknowledged_count}
                </div>
                <div className="text-xs text-green-600">Acknowledged</div>
              </div>
              <div className="text-center p-3 bg-amber-50 rounded-lg">
                <div className="text-2xl font-bold text-amber-700">
                  {ackStatus.outstanding_users.length}
                </div>
                <div className="text-xs text-amber-600">Outstanding</div>
              </div>
            </div>

            {ackStatus.outstanding_users.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Outstanding Users</h4>
                <div className="space-y-1">
                  {ackStatus.outstanding_users.map((u) => (
                    <div key={u.id} className="flex items-center gap-2 text-sm text-gray-600">
                      <Clock className="h-3 w-3 text-amber-500" />
                      <span>{u.username}</span>
                      <span className="text-gray-400">({u.email})</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {ackStatus.outstanding_users.length === 0 && ackStatus.total_targeted > 0 && (
              <div className="flex items-center gap-2 text-sm text-green-600">
                <CheckCircle2 className="h-4 w-4" />
                All targeted users have acknowledged this communication.
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
